import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Search, ShieldCheck, KeyRound, LinkIcon, ShieldQuestion, UserX, Users, Wrench, Fingerprint, MessageSquare, BookUser, FileLock2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../ui/input";

// Contenu du centre d'aide : language pack « help » (catégories, articles, comparatif).
const CATEGORY_ICONS = {
  "bien-demarrer": ShieldCheck, generateur: KeyRound, lien: LinkIcon, sms: MessageSquare, fichiers: FileLock2,
  "secure-request": ShieldQuestion, annuaire: BookUser, anonyme: UserX, comptes: Users,
  tester: Fingerprint, depannage: Wrench,
};

const ComparisonTable = () => {
  const { t } = useTranslation("help");
  const h = t("comparison.headers", { returnObjects: true });
  const rows = t("comparison.rows", { returnObjects: true });
  return (
    <div data-testid="doc-comparison-table" className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left">
            {["feature", "anon", "free", "paid"].map((k) => (
              <th key={k} className="py-2.5 px-4 font-semibold text-slate-700">{h[k]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.feature} className="border-t border-slate-100">
              <td className="py-2.5 px-4 text-slate-700">{r.feature}</td>
              <td className={`py-2.5 px-4 ${r.anonAvailable ? "text-slate-600" : "text-slate-400"}`}>{r.anon}</td>
              <td className="py-2.5 px-4 text-slate-600">{r.free}</td>
              <td className="py-2.5 px-4 text-slate-600">{r.paid}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Article = ({ q, text, render }) => (
  <details className="group rounded-lg border border-slate-200 bg-white p-4" data-testid="doc-article">
    <summary className="cursor-pointer list-none font-medium text-slate-800 flex items-center justify-between gap-3">
      <span>{q}</span>
      <span className="text-slate-400 group-open:rotate-180 transition-transform shrink-0">▾</span>
    </summary>
    <div className="mt-3 text-sm text-slate-600 leading-relaxed">
      {render ? render() : <p>{text}</p>}
    </div>
  </details>
);

export const DocumentationPage = () => {
  const { t, i18n } = useTranslation("help");
  const [query, setQuery] = useState("");
  const lang = i18n.resolvedLanguage;
  const CATEGORIES = useMemo(() => Object.entries(t("categories", { returnObjects: true }))
    .map(([key, label]) => ({ key, label, icon: CATEGORY_ICONS[key] || LifeBuoy })), [t, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const ARTICLES = useMemo(() => t("articles", { returnObjects: true }), [t, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ARTICLES;
    return ARTICLES.filter((a) => {
      const cat = CATEGORIES.find((c) => c.key === a.cat)?.label || "";
      return (`${a.q} ${a.text} ${cat}`).toLowerCase().includes(q);
    });
  }, [query, ARTICLES, CATEGORIES]);

  const byCat = (key) => filtered.filter((a) => a.cat === key);
  const visibleCategories = CATEGORIES.filter((c) => byCat(c.key).length > 0);

  return (
    <div data-testid="documentation-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
          <LifeBuoy className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-3xl font-heading font-extrabold tracking-tight">{t("title")}</h1>
          <p className="text-slate-500 text-sm">{t("subtitle")}</p>
        </div>
      </div>

      {/* Recherche 100% frontend (aucun appel réseau, aucun analytics) */}
      <div className="relative mb-6">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="doc-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("searchPlaceholder")}
          className="pl-9 bg-white border-slate-200"
          aria-label={t("searchLabel")}
        />
      </div>

      {/* Navigation légère par catégorie (ancres, accessible au clavier, sans réseau) */}
      {visibleCategories.length > 0 && (
        <nav data-testid="doc-category-nav" aria-label={t("categoryNav")} className="flex flex-wrap gap-2 mb-8">
          {visibleCategories.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.key}
                href={`#${c.key}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
              >
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </a>
            );
          })}
        </nav>
      )}

      {visibleCategories.length === 0 ? (
        <div data-testid="doc-no-results" className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          {t("noResults", { query })}
        </div>
      ) : (
        <div className="space-y-10">
          {visibleCategories.map((c) => {
            const Icon = c.icon;
            return (
              <section key={c.key} id={c.key} data-testid={`doc-section-${c.key}`} className="scroll-mt-32">
                <h2 className="flex items-center gap-2 text-lg font-heading font-bold text-slate-900 mb-3">
                  <Icon className="w-5 h-5 text-emerald-600" /> {c.label}
                </h2>
                <div className="space-y-2">
                  {byCat(c.key).map((a) => (
                    <Article key={a.q} q={a.q} text={a.text} render={a.render} />
                  ))}
                </div>
                {c.key === "bien-demarrer" && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-600 leading-relaxed mb-2">
                      <b>{t("anonVsFree")}</b> {t("anonVsFreeIntro")}
                    </p>
                    <ComparisonTable />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <p className="mt-10 text-sm text-slate-500">
        {t("moreHelp")} <Link to="/contact" data-testid="doc-contact-link" className="text-emerald-600 underline">{t("contactUs")}</Link>.
      </p>
    </div>
  );
};

export default DocumentationPage;
